// ═══════════════════════════════════════════════════════════
// ToastContext — система сповіщень (success, error, info)
// useToast() → { success(msg), error(msg), info(msg) }
// ═══════════════════════════════════════════════════════════
import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import ToastContainer from '../components/ToastContainer';

const ToastContext = createContext(null);

const MAX_TOASTS = 3;
const AUTO_DISMISS = 3000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, removing: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  // opts may carry an `action: { label, onClick }` to render an inline
  // button alongside the message — used by handlers that want the user
  // to act on the toast (e.g. 'Open existing customer' on duplicate-
  // phone). Toasts with an action stay visible 6s instead of 3 so the
  // user has time to read + click.
  const addToast = useCallback((message, type = 'info', opts = {}) => {
    const id = ++idRef.current;
    setToasts(prev => {
      const next = [...prev, { id, message, type, action: opts.action || null, removing: false }];
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });
    const dismissAfter = opts.action ? AUTO_DISMISS * 2 : AUTO_DISMISS;
    setTimeout(() => removeToast(id), dismissAfter);
  }, [removeToast]);

  const toast = useMemo(() => ({
    success: (msg, opts) => addToast(msg, 'success', opts),
    error:   (msg, opts) => addToast(msg, 'error',   opts),
    info:    (msg, opts) => addToast(msg, 'info',    opts),
  }), [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
